import { ReactNode } from 'react';
import { Box, Typography } from '@mui/material';

interface Props {
  from: string;
  to: string;
  /** Salvedad del punto de llegada (duración aproximada, aclaración). */
  toNote?: ReactNode;
}

/**
 * El par origen–destino del arquetipo de producto: dos puntos unidos por un
 * riel, sin caja ni borde alrededor. La caja gris que envolvía esto antes era
 * decoración — el riel ya dice que los dos puntos son un recorrido, y el borde
 * solo gastaba altura de card.
 *
 * Los puntos son los que RESOLVIÓ el proveedor, que pueden no ser exactamente
 * los pedidos (se pide el 37 y devuelve el 39): por eso se muestran completos
 * y no resumidos en un título corrido.
 */
export function RoutePair({ from, to, toNote }: Props) {
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: '10px minmax(0, 1fr)',
        columnGap: 1,
      }}
    >
      <Box
        sx={(theme) => ({
          gridRow: '1 / 3',
          justifySelf: 'center',
          position: 'relative',
          width: '2px',
          bgcolor: theme.palette.divider,
          '&::before, &::after': {
            content: '""',
            position: 'absolute',
            left: '-2.5px',
            width: '7px',
            height: '7px',
            bgcolor: theme.palette.background.paper,
            border: '2px solid',
          },
          '&::before': {
            top: '2px',
            borderRadius: '50%',
            borderColor: theme.palette.text.disabled,
          },
          // El destino se marca distinto: es donde termina el traslado.
          '&::after': {
            bottom: '2px',
            borderRadius: '2px',
            borderColor: theme.palette.primary.main,
          },
        })}
      />
      <Typography sx={{ fontSize: 13, fontWeight: 500, py: '1px' }}>{from}</Typography>
      <Typography sx={{ fontSize: 13, fontWeight: 500, py: '1px' }}>
        {to}
        {toNote && (
          <Box component="span" color="text.secondary" sx={{ fontWeight: 400, ml: 0.5 }}>
            {toNote}
          </Box>
        )}
      </Typography>
    </Box>
  );
}
